import { Link, router } from '@inertiajs/react'
import { useState } from 'react'
import InfoTip from '../../../components/InfoTip'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatAt(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function formatDate(dateStr) {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SEGMENT_META = {
  airline:    { icon: '✈',  label: 'Airline Segment',     color: '#2196F3', provider_icon: '🛫' },
  hotel:      { icon: '🏨', label: 'Hotel Segment',        color: '#9C27B0', provider_icon: '🏨' },
  car_rental: { icon: '🚗', label: 'Car Rental Segment',   color: '#FF9800', provider_icon: '🚗' },
  cruise:     { icon: '🚢', label: 'Cruise Segment',       color: '#00BCD4', provider_icon: '🚢' },
}

const CARRIER_COLORS = {
  B6: '#003087', DL: '#E01933', UA: '#005DAA', AA: '#0078D2', WN: '#304CB2',
}

const SEVERITY_META = {
  critical: { color: '#F44336', label: 'Critical gap' },
  high:     { color: '#FF9800', label: 'High risk gap' },
}

// ─── Timeline connector ────────────────────────────────────────────────────────

function TimelineConnector({ gapRisk }) {
  if (!gapRisk) {
    return <div className="flex justify-center py-2"><div className="w-px h-8 bg-gray-200" /></div>
  }
  const meta = SEVERITY_META[gapRisk.severity] || SEVERITY_META.high
  return (
    <div className="flex flex-col items-center py-2 gap-1">
      <div className="w-px h-4" style={{ background: meta.color }} />
      <div
        className="px-3 py-1 rounded text-xs font-bold border"
        style={{ borderColor: meta.color, color: meta.color, background: meta.color + '15' }}
      >
        ⚠ {meta.label} — {gapRisk.gap_hours}h gap ({gapRisk.from} → {gapRisk.to})
      </div>
      <div className="w-px h-4" style={{ background: meta.color }} />
    </div>
  )
}

// ─── Segment card ─────────────────────────────────────────────────────────────

function SegmentCard({ segment, gapRisk }) {
  const [expanded, setExpanded] = useState(false)
  const meta        = SEGMENT_META[segment.segment_type] || SEGMENT_META.airline
  const carrierColor = CARRIER_COLORS[segment.carrier_code] || '#607D8B'
  const riskColor = segment.risk_score >= 0.7 ? '#F44336' : segment.risk_score >= 0.4 ? '#FF9800' : '#4CAF50'

  const crs     = segment.crs_refs
  const hotel   = crs?.hotel
  const car     = crs?.car_rental

  const lastEvent = segment.last_event

  return (
    <div>
      <div
        className="bg-white rounded-lg border-2 overflow-hidden"
        style={{ borderColor: gapRisk ? SEVERITY_META[gapRisk.severity]?.color || '#FF9800' : meta.color }}
      >
        {/* Segment header */}
        <div
          className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50"
          style={{ borderBottom: `2px solid ${meta.color}22` }}
          onClick={() => setExpanded(e => !e)}
        >
          {/* Icon */}
          <div
            className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-xl"
            style={{ background: meta.color + '22' }}
          >
            {meta.icon}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-sm" style={{ color: meta.color }}>{meta.label}</span>
              <span className="font-mono font-bold text-sm text-gray-900">{segment.pnr_locator}</span>
              {segment.carrier_code && (
                <span className="font-mono font-bold text-xs" style={{ color: carrierColor }}>{segment.carrier_code}</span>
              )}
              {segment.cabin_class && (
                <span className="text-xs bg-gray-100 text-gray-600 px-1.5 rounded font-mono">{segment.cabin_class}</span>
              )}
            </div>

            {/* Provider-specific summary */}
            <p className="text-xs text-gray-500 mt-0.5">
              {segment.segment_type === 'airline' && segment.gds_locator && `Sabre GDS: ${segment.gds_locator}`}
              {segment.segment_type === 'hotel' && hotel && `Hilton CRS: ${hotel.pnr || '—'} · ${hotel.property_code || ''}`}
              {segment.segment_type === 'car_rental' && car && `National IDS: ${car.pnr || '—'} · Pickup: ${car.pickup_iata || '—'}`}
            </p>
          </div>

          {/* Risk + status */}
          <div className="flex-shrink-0 text-right">
            {segment.risk_score != null && (
              <p className="text-sm font-bold tabular-nums" style={{ color: riskColor }}>
                {segment.risk_score.toFixed(2)}
              </p>
            )}
            {segment.status && (
              <p className="text-xs text-gray-500 capitalize">{segment.status}</p>
            )}
          </div>

          <span className="text-gray-400 flex-shrink-0">{expanded ? '▲' : '▼'}</span>
        </div>

        {/* Expanded detail */}
        {expanded && (
          <div className="px-4 py-3 text-xs space-y-3">
            {/* Airline detail */}
            {segment.segment_type === 'airline' && (
              <div className="grid grid-cols-2 gap-3">
                {segment.gds_locator && (
                  <div>
                    <p className="text-gray-400">GDS Locator (Sabre)</p>
                    <p className="font-mono font-bold text-gray-800">{segment.gds_locator}</p>
                  </div>
                )}
                {segment.carrier_tier && (
                  <div>
                    <p className="text-gray-400">Loyalty Tier</p>
                    <p className="font-bold" style={{ color: carrierColor }}>{segment.carrier_tier}</p>
                  </div>
                )}
              </div>
            )}

            {/* Hotel CRS detail */}
            {hotel && (
              <div>
                <p className="font-semibold text-purple-700 mb-1.5">🏨 Hilton CRS Data</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 bg-purple-50 border border-purple-200 rounded p-2.5">
                  <div><span className="text-gray-400">CRS PNR:</span> <span className="font-mono">{hotel.pnr || '—'}</span></div>
                  <div><span className="text-gray-400">Property:</span> {hotel.property_code || '—'}</div>
                  <div><span className="text-gray-400">Room:</span> {hotel.room_type || '—'}</div>
                  <div><span className="text-gray-400">Rate Plan:</span> {hotel.rate_plan || '—'}</div>
                  <div><span className="text-gray-400">Check-in:</span> {formatDate(hotel.check_in)}</div>
                  <div><span className="text-gray-400">Check-out:</span> {formatDate(hotel.check_out)}</div>
                  {hotel.last_crs_event && (
                    <div className="col-span-2">
                      <span className="text-gray-400">Last CRS Event:</span> {hotel.last_crs_event}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Car rental IDS detail */}
            {car && (
              <div>
                <p className="font-semibold text-orange-700 mb-1.5">🚗 National IDS Data</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 bg-orange-50 border border-orange-200 rounded p-2.5">
                  <div><span className="text-gray-400">IDS Reservation:</span> <span className="font-mono">{car.pnr || '—'}</span></div>
                  <div><span className="text-gray-400">Pickup IATA:</span> {car.pickup_iata || '—'}</div>
                  <div><span className="text-gray-400">Vehicle Class:</span> {car.vehicle_class || '—'}</div>
                  {car.pickup_dt && <div><span className="text-gray-400">Pickup:</span> {formatAt(car.pickup_dt)}</div>}
                  {car.last_ids_event && (
                    <div className="col-span-2">
                      <span className="text-gray-400">Last IDS Event:</span> {car.last_ids_event}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* IBM events */}
            {lastEvent && (
              <div>
                <p className="font-semibold text-gray-500 mb-1">Last IBM ADK Event</p>
                <div className="bg-gray-50 border border-gray-200 rounded p-2">
                  <p className="font-mono text-gray-700">{lastEvent.event_type}</p>
                  {lastEvent.occurred_at && <p className="text-gray-400">{formatAt(lastEvent.occurred_at)}</p>}
                </div>
              </div>
            )}

            {/* Action links */}
            <div className="flex gap-2 flex-wrap">
              <a
                href={`/admin/cloudtraq_gds/pnr/${segment.pnr_locator}`}
                className="text-xs px-3 py-1.5 bg-indigo-50 border border-indigo-200 text-indigo-700 rounded hover:bg-indigo-100"
              >
                Full PNR Detail →
              </a>
              <button
                onClick={() => {
                  fetch(`/admin/cloudtraq_gds/pnr/${segment.pnr_locator}/ava`, {
                    method: 'POST',
                    headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content },
                  }).then(() => router.reload())
                }}
                className="text-xs px-3 py-1.5 bg-purple-50 border border-purple-200 text-purple-700 rounded hover:bg-purple-100"
              >
                🧠 Force AVA
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Gap risk connector */}
      {gapRisk && <TimelineConnector gapRisk={gapRisk} />}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function MasterPnr({ itinerary_ref, segments, gap_risks, tenant_branding }) {
  // Map segment_type → gap risk (from airline to next segment)
  const gapByFrom = {}
  gap_risks.forEach(g => { gapByFrom[g.from] = g })

  // Determine segment display order
  const ORDER = ['airline', 'hotel', 'car_rental', 'cruise', 'rail']
  const sorted = [...segments].sort((a, b) => {
    const ai = ORDER.indexOf(a.segment_type)
    const bi = ORDER.indexOf(b.segment_type)
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi)
  })

  const hasGaps = gap_risks.length > 0

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Master Itinerary</h1>
            <p className="text-sm text-gray-500 mt-1">
              <span className="font-mono font-bold text-indigo-600">{itinerary_ref}</span>
              {' · '}
              {segments.length} segment{segments.length !== 1 ? 's' : ''}
              {' ('}
              {segments.map(s => (s.segment_type || 'airline')).join(' + ')}
              {')'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => router.reload()} className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100">
              Refresh
            </button>
            <Link href="/admin/cloudtraq_gds" className="text-sm text-blue-600 hover:underline">← Command Center</Link>
          </div>
        </div>

        {/* Gap risk banner */}
        {hasGaps && (
          <div className="mb-4 bg-orange-50 border border-orange-300 rounded-lg px-4 py-3 text-xs">
            <p className="font-semibold text-orange-800 mb-1">
              ⚠ Itinerary Gap Risk Detected
            </p>
            {gap_risks.map((g, i) => {
              const meta = SEVERITY_META[g.severity] || SEVERITY_META.high
              return (
                <p key={i} style={{ color: meta.color }}>
                  <strong>{meta.label}</strong>: {g.gap_hours}h between {g.from} arrival and {g.to} check-in/pickup
                </p>
              )
            })}
            <p className="text-orange-600 mt-1">
              If the airline IROP causes a delay, downstream hotel/car segments may be at risk.
            </p>
          </div>
        )}

        {/* Timeline */}
        {segments.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <p className="text-gray-400 text-sm">No segments found for itinerary <code>{itinerary_ref}</code>.</p>
          </div>
        ) : (
          <div>
            {sorted.map((segment, i) => {
              const nextSeg = sorted[i + 1]
              const gap     = nextSeg ? gapByFrom[segment.segment_type] : null
              return (
                <div key={segment.pnr_locator}>
                  <SegmentCard segment={segment} gapRisk={null} />
                  {nextSeg && <TimelineConnector gapRisk={gap} />}
                </div>
              )
            })}
          </div>
        )}

        {/* Segment legend */}
        <div className="mt-4 flex gap-4 flex-wrap text-xs text-gray-500">
          {Object.entries(SEGMENT_META).map(([key, { icon, label, color }]) => (
            <span key={key} className="flex items-center gap-1" style={{ color }}>
              {icon} {label}
            </span>
          ))}
        </div>

        {/* Nav */}
        <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-500">
          <Link href="/admin/cloudtraq_gds" className="text-blue-600 hover:underline">← Command Center</Link>
          <Link href="/admin/cloudtraq_gds/pnr_mutations" className="text-blue-600 hover:underline">PNR Mutations</Link>
          <Link href="/admin/cloudtraq_gds/high_risk" className="text-blue-600 hover:underline">High Risk Candidates</Link>
        </div>
      </div>
    </div>
  )
}
